import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import apiClient from "../services/apiClient";
import { gtmTrackConversion } from "../hooks/useGTM";

/**
 * Possible verification states for the page.
 */
type VerifyStatus = "loading" | "success" | "error";

/**
 * Error types returned by POST /auth/verify-email.
 */
type VerifyError =
  | "TOKEN_EXPIRED"
  | "TOKEN_INVALID"
  | "ALREADY_VERIFIED"
  | "USER_INACTIVE"
  | "NETWORK_ERROR"
  | "NO_TOKEN";

/**
 * Human-readable error messages in Polish.
 */
const ERROR_MESSAGES: Record<VerifyError, string> = {
  TOKEN_EXPIRED: "Link wygasł.",
  TOKEN_INVALID: "Link jest nieprawidłowy.",
  ALREADY_VERIFIED: "Email został już potwierdzony.",
  USER_INACTIVE: "Konto zostało dezaktywowane.",
  NETWORK_ERROR: "Błąd połączenia - odśwież stronę.",
  NO_TOKEN: "Brak tokenu weryfikacyjnego w linku.",
};

/**
 * Errors that should show the "resend link" form.
 */
const RESENDABLE_ERRORS: VerifyError[] = [
  "TOKEN_EXPIRED",
  "TOKEN_INVALID",
  "NO_TOKEN",
];

/**
 * Map internal VerifyError codes to GA4 email_verification_result values.
 * Spec result enum: success | expired | invalid | already_verified
 */
function mapVerifyErrorToResult(
  error: VerifyError
): "expired" | "invalid" | "already_verified" {
  switch (error) {
    case "TOKEN_EXPIRED":
      return "expired";
    case "ALREADY_VERIFIED":
      return "already_verified";
    default:
      return "invalid";
  }
}

/**
 * Try to extract email from JWT payload (base64 middle part).
 * Used to prefill email input on error states (AC-15).
 */
function extractEmailFromToken(token: string): string | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.email === "string" ? payload.email : null;
  } catch {
    return null;
  }
}

/**
 * VerifyEmail Page
 *
 * AC-13: Reads `token` from query params, calls POST /auth/verify-email,
 * and renders the result.
 *
 * AC-14: Loading, success, and error states with proper ARIA attributes.
 *
 * AC-15: On expired/invalid error, shows "resend link" form with email input.
 *
 * Layout: centered card, consistent with MagicLinkVerify.tsx.
 */
const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const [status, setStatus] = useState<VerifyStatus>("loading");
  const [errorType, setErrorType] = useState<VerifyError | null>(null);
  // Set page title based on verification status
  useEffect(() => {
    const titles: Record<VerifyStatus, string> = {
      loading: "Weryfikacja email...",
      success: "Email potwierdzony",
      error: "Weryfikacja nie powiodła się",
    };
    document.title = titles[status];
  }, [status]);

  // Resend form state (AC-15)
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      if (!token) {
        setStatus("error");
        setErrorType("NO_TOKEN");
        // Event #11: email_verification_result — no token = invalid
        gtmTrackConversion("email_verification_result", { result: "invalid" });
        return;
      }

      // Try to extract email from JWT for prefilling (AC-15)
      const emailFromToken = extractEmailFromToken(token);
      if (emailFromToken) {
        setResendEmail(emailFromToken);
      }

      try {
        const { data } = await apiClient.post("/auth/verify-email", {
          token,
        });

        // ALREADY_VERIFIED is a success from the user's perspective — the email
        // IS confirmed. It legitimately happens whenever the confirmation link is
        // processed more than once for the same account: a double-click, an
        // email-security scanner that pre-fetches the link, or a re-registration
        // that reuses an already-verified user (the verification email is still
        // sent, so its link is "dead on arrival"). Rendering a red error there is
        // misleading, so we show the success screen. Analytics keeps the
        // success / already_verified distinction for funnel accuracy.
        if (data.success || data.error === "ALREADY_VERIFIED") {
          setStatus("success");
          gtmTrackConversion("email_verification_result", {
            result: data.success ? "success" : "already_verified",
          });
        } else {
          setStatus("error");
          setErrorType(data.error || "TOKEN_INVALID");
          // Event #11: email_verification_result — map error to result
          gtmTrackConversion("email_verification_result", {
            result: mapVerifyErrorToResult(data.error || "TOKEN_INVALID"),
          });
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { error?: string } } };
        const errCode: VerifyError =
          (err.response?.data?.error as VerifyError) || "NETWORK_ERROR";
        // Same idempotent treatment if the API ever surfaces ALREADY_VERIFIED
        // as a non-2xx response instead of a 200 with { success: false }.
        if (errCode === "ALREADY_VERIFIED") {
          setStatus("success");
          gtmTrackConversion("email_verification_result", {
            result: "already_verified",
          });
          return;
        }
        setStatus("error");
        setErrorType(errCode);
        // Event #11: email_verification_result — map error to result
        gtmTrackConversion("email_verification_result", {
          result: mapVerifyErrorToResult(errCode),
        });
      }
    };

    verifyToken();
  }, [location.search]);

  // AC-15: Handle resend verification email
  const handleResend = useCallback(async () => {
    if (!resendEmail || resendLoading) return;

    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    try {
      await apiClient.post("/auth/resend-verification", {
        email: resendEmail,
      });
      setResendMessage(
        "Jeśli konto istnieje, nowy link weryfikacyjny został wysłany."
      );
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err.response?.status === 429) {
        setResendError("Poczekaj chwilę przed ponownym wysłaniem.");
      } else {
        setResendError("Wystąpił błąd. Spróbuj ponownie.");
      }
    } finally {
      setResendLoading(false);
    }
  }, [resendEmail, resendLoading]);

  const showResendForm =
    errorType && RESENDABLE_ERRORS.includes(errorType);

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
          {/* AC-14: Loading state */}
          {status === "loading" && (
            <div role="status" aria-live="polite">
              <div className="flex justify-center mb-4">
                <svg
                  className="animate-spin h-10 w-10 text-primary-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-neutral-900">
                Weryfikacja adresu email...
              </h2>
              <p className="text-neutral-600 mt-2">Proszę czekać.</p>
            </div>
          )}

          {/* AC-14: Success state */}
          {status === "success" && (
            <div role="status" aria-live="polite">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-green-600">
                Email potwierdzony!
              </h2>
              <p className="text-neutral-600 mt-2">
                Możesz się teraz zalogować.
              </p>
              <Link
                to="/login"
                className="inline-block mt-6 bg-primary-700 hover:bg-primary-800 text-white font-semibold py-2 px-6 rounded-lg transition"
              >
                Przejdź do logowania
              </Link>
            </div>
          )}

          {/* AC-14: Error states */}
          {status === "error" && errorType && (
            <div role="alert">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>

              {/* ALREADY_VERIFIED is handled as success (see verify effect),
                  so it never reaches this error branch. */}
              <h2 className="text-2xl font-semibold text-red-600">
                Weryfikacja nie powiodła się
              </h2>
              <p className="text-neutral-600 mt-2">
                {ERROR_MESSAGES[errorType]}
              </p>

              {/* AC-15: Resend form for expired/invalid tokens */}
              {showResendForm && (
                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <p className="text-sm text-neutral-600 mb-3">
                    Wyślij nowy link weryfikacyjny:
                  </p>

                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="Twój adres email"
                      aria-label="Adres email do wysłania nowego linku weryfikacyjnego"
                      autoComplete="email"
                      required
                      className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                    <button
                      onClick={handleResend}
                      disabled={resendLoading || !resendEmail}
                      className="bg-primary-700 hover:bg-primary-800 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition text-sm whitespace-nowrap"
                    >
                      {resendLoading ? (
                        <span className="inline-flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-1 h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Wysyłanie...
                        </span>
                      ) : (
                        "Wyślij nowy link"
                      )}
                    </button>
                  </div>

                  {resendMessage && (
                    <p
                      className="text-sm text-green-600 mt-2"
                      role="status"
                    >
                      {resendMessage}
                    </p>
                  )}

                  {resendError && (
                    <p className="text-sm text-red-600 mt-2" role="alert">
                      {resendError}
                    </p>
                  )}
                </div>
              )}

              {/* Network error -> refresh hint */}
              {errorType === "NETWORK_ERROR" && (
                <button
                  onClick={() => window.location.reload()}
                  className="inline-block mt-6 bg-primary-700 hover:bg-primary-800 text-white font-semibold py-2 px-6 rounded-lg transition"
                >
                  Odśwież stronę
                </button>
              )}

              {/* Login link for all error states */}
              {errorType !== "NETWORK_ERROR" && (
                <p className="text-sm text-neutral-500 mt-4">
                  <Link
                    to="/login"
                    className="text-primary-700 hover:text-primary-800 underline"
                  >
                    Przejdź do strony logowania
                  </Link>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
